import { z } from "zod";
import { pricing } from "./pricing.js";
import { getCaptionPreset } from "./presets.js";
/**
 * Caption styles for the web preview and the burned-in render (docs/spec.md §11).
 * The same CaptionStyle drives both, so the preview matches the MP4.
 */
export const CAPTION_FONT_IDS = ["grotesk", "condensed", "serif"];
/** Bundled OFL fonts. All cover Albanian (ë Ë ç Ç). The same files ship in the web app and the ASR image. */
export const CAPTION_FONTS = Object.freeze({
    grotesk: {
        id: "grotesk",
        family: "Schibsted Grotesk",
        weight: 700,
        file: "SchibstedGrotesk-Bold.ttf",
        path: "fonts/SchibstedGrotesk-Bold.ttf",
    },
    condensed: {
        id: "condensed",
        family: "Archivo Condensed",
        weight: 700,
        file: "ArchivoCondensed-Bold.ttf",
        path: "fonts/ArchivoCondensed-Bold.ttf",
    },
    serif: {
        id: "serif",
        family: "Instrument Serif",
        weight: 400,
        file: "InstrumentSerif-Regular.ttf",
        path: "fonts/InstrumentSerif-Regular.ttf",
    },
});
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #RRGGBB");
export const captionStyleSchema = z.object({
    font: z.enum(CAPTION_FONT_IDS),
    /** Font size as a percentage of the frame height. */
    sizePct: z.number().min(2).max(8),
    color: hexColor,
    background: z.enum(["none", "outline", "box"]),
    backgroundColor: hexColor,
    /** 0–1; used by `box`. */
    backgroundOpacity: z.number().min(0).max(1),
    /** Placed inside the preset's safe area. */
    position: z.enum(["bottom", "middle", "top"]),
    uppercase: z.boolean(),
    /** Word-by-word highlight from word timings; `null` = off. */
    highlightColor: hexColor.nullable(),
    /** Background for audio-only sources. */
    canvasColor: hexColor,
});
/** Partial style as sent by clients; unspecified keys come from `defaultStyleFor(preset)`. */
export const captionStylePatchSchema = captionStyleSchema.partial().strict();
/** Style before the preset's `fontScale` is applied (`sizePct` 5 = the YouTube preset). */
export const DEFAULT_CAPTION_STYLE = Object.freeze({
    font: "grotesk",
    sizePct: 5,
    color: "#FFFFFF",
    background: "box",
    backgroundColor: "#000000",
    backgroundOpacity: 0.6,
    position: "bottom",
    uppercase: false,
    highlightColor: null,
    canvasColor: "#0B1020",
});
/** Default style for a preset: `DEFAULT_CAPTION_STYLE` with `sizePct = preset.fontScale × 100`. */
export function defaultStyleFor(presetId) {
    const preset = getCaptionPreset(presetId);
    return { ...DEFAULT_CAPTION_STYLE, sizePct: Math.round(preset.fontScale * 1000) / 10 };
}
/** `defaultStyleFor(preset)` overlaid with a validated patch (`captionStylePatchSchema`). */
export function resolveCaptionStyle(presetId, patch) {
    return { ...defaultStyleFor(presetId), ...(patch ?? {}) };
}
/** Output frame per aspect ratio (spec §11). */
export const OUTPUT_FRAMES = Object.freeze({
    "16:9": Object.freeze({ width: 1920, height: 1080 }),
    "9:16": Object.freeze({ width: 1080, height: 1920 }),
    "4:5": Object.freeze({ width: 1080, height: 1350 }),
    "1:1": Object.freeze({ width: 1080, height: 1080 }),
});
export const RENDER_FRAMINGS = ["fit", "crop"];
/** Frame size of the preset's aspect ratio. */
export function frameFor(presetId) {
    return { ...OUTPUT_FRAMES[getCaptionPreset(presetId).aspect] };
}
/** Limits of burned-in renders (spec §11). The numbers live in `pricing.render`. */
export const RENDER_LIMITS = Object.freeze({
    /** Renders per account that may be queued or processing at the same time. */
    maxActive: pricing.render.maxActive,
    /** Longest source media, in seconds. */
    maxSourceSeconds: pricing.render.maxSourceSeconds,
    /** Days until the rendered MP4 is deleted. */
    retentionDays: 30,
    /** Attempts before a render fails. */
    maxAttempts: 3,
});
